import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'

type ReasoningTrailTokens = {
  cardBg: string
  cardBorder: string
  title: string
  subtitle: string
  muted: string
  decisive: string
  chevron: string
  descMuted: string
  descDecisive: string
  evidenceBg: string
  evidenceBorder: string
  evidenceLabel: string
  evidenceValue: string
  link: string
  footerDivider: string
}

// Every value is a semantic token (design-library governance: never bind to a
// primitive or raw hex). Dark tokens sit at the top level; light counterparts
// under the `light.*` namespace.
const tokens: Record<ColorScheme, ReasoningTrailTokens> = {
  dark: {
    cardBg: 'bg.card',
    cardBorder: 'interactive.tertiary-border',
    title: 'text.primary',
    subtitle: 'text.secondary',
    muted: 'interactive.tertiary-text',
    decisive: 'status.success',
    chevron: 'text.tertiary',
    descMuted: 'text.tertiary',
    descDecisive: 'text.secondary',
    evidenceBg: 'bg.input',
    evidenceBorder: 'border.subtle',
    evidenceLabel: 'text.tertiary',
    evidenceValue: 'text.primary',
    link: 'text.link',
    footerDivider: 'interactive.tertiary-border',
  },
  light: {
    cardBg: 'light.bg.card',
    cardBorder: 'light.interactive.tertiary-border',
    title: 'light.text.primary',
    subtitle: 'light.text.secondary',
    muted: 'light.interactive.tertiary-text',
    decisive: 'light.status.success',
    chevron: 'light.text.tertiary',
    descMuted: 'light.text.tertiary',
    descDecisive: 'light.text.secondary',
    evidenceBg: 'light.bg.input',
    evidenceBorder: 'light.border.subtle',
    evidenceLabel: 'light.text.tertiary',
    evidenceValue: 'light.text.primary',
    link: 'light.text.link',
    footerDivider: 'light.interactive.tertiary-border',
  },
}

// Decisive rows (of a resolved trail) are the only recolored rows; everything
// else is muted.
const accent = (colorScheme: ColorScheme, emphasized: boolean): string =>
  emphasized ? tokens[colorScheme].decisive : tokens[colorScheme].muted

// ─── Card ───────────────────────────────────────────────────────────────────

export const cardStyles = (colorScheme: ColorScheme): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
  bg: tokens[colorScheme].cardBg,
  border: '1px solid',
  borderColor: tokens[colorScheme].cardBorder,
  p: '16px',
})

export const cardStepListStyles: SystemStyleObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  w: 'full',
}

export const cardFooterStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  borderTop: '1px solid',
  borderTopColor: tokens[colorScheme].footerDivider,
  pt: '10px',
  w: 'full',
})

// ─── Header ───────────────────────────────────────────────────────────────────

export const headerStyles: SystemStyleObject = {
  display: 'flex',
  alignItems: 'center',
  gap: '9px',
  w: 'full',
}

export const headerIconStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  color: tokens[colorScheme].title,
  flexShrink: 0,
  h: '20px',
  w: '20px',
})

export const headerTextGroupStyles: SystemStyleObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
}

export const headerTitleStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  color: tokens[colorScheme].title,
  m: 0,
  textStyle: 'label',
})

export const headerSubtitleStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  color: tokens[colorScheme].subtitle,
  m: 0,
  textStyle: 'caption',
})

// ─── Row ────────────────────────────────────────────────────────────────────

export const rowRootStyles: SystemStyleObject = { w: 'full' }

export const rowTriggerStyles: SystemStyleObject = {
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  gap: '10px',
  py: '5px',
  textAlign: 'left',
  w: 'full',
}

export const checkboxStyles = (
  colorScheme: ColorScheme,
  emphasized: boolean
): SystemStyleObject => ({
  display: 'flex',
  alignItems: 'center',
  border: '1.5px solid',
  borderColor: accent(colorScheme, emphasized),
  color: accent(colorScheme, emphasized),
  flexShrink: 0,
  h: '16px',
  justifyContent: 'center',
  w: '16px',
})

export const checkboxGlyphStyles: SystemStyleObject = { h: '11px', w: '11px' }

export const rowLabelStyles = (
  colorScheme: ColorScheme,
  emphasized: boolean
): SystemStyleObject => ({
  color: accent(colorScheme, emphasized),
  flex: '1 0 0',
  m: 0,
  minW: 0,
  textAlign: 'left',
  textStyle: 'label',
})

export const rowResultStyles = (
  colorScheme: ColorScheme,
  emphasized: boolean
): SystemStyleObject => ({
  color: accent(colorScheme, emphasized),
  flexShrink: 0,
  m: 0,
  textStyle: 'small-label',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
})

export const chevronStyles = (
  colorScheme: ColorScheme,
  isExpanded: boolean
): SystemStyleObject => ({
  color: tokens[colorScheme].chevron,
  flexShrink: 0,
  h: '14px',
  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
  transition: 'transform 0.2s ease',
  w: '14px',
})

export const rowDetailStyles: SystemStyleObject = { pb: '6px', pl: '24px' }

export const rowDescriptionStyles = (
  colorScheme: ColorScheme,
  emphasized: boolean
): SystemStyleObject => ({
  color: emphasized
    ? tokens[colorScheme].descDecisive
    : tokens[colorScheme].descMuted,
  m: 0,
  textStyle: 'caption',
})

export const rowMetadataWrapperStyles: SystemStyleObject = { mt: '9px' }

export const rowLinkStyles = (colorScheme: ColorScheme): SystemStyleObject => ({
  color: tokens[colorScheme].link,
  cursor: 'pointer',
  display: 'inline-block',
  mt: '9px',
  textStyle: 'caption-medium',
})

// ─── Metadata card ─────────────────────────────────────────────────────────

export const metadataCardStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  bg: tokens[colorScheme].evidenceBg,
  border: '1px solid',
  borderColor: tokens[colorScheme].evidenceBorder,
  px: '12px',
  py: '9px',
})

export const metadataTitleStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  color: tokens[colorScheme].evidenceLabel,
  m: 0,
  mb: '7px',
  textStyle: 'small-label',
  textTransform: 'uppercase',
})

export const metadataRowStyles: SystemStyleObject = {
  display: 'flex',
  gap: '16px',
  py: '2px',
}

export const metadataRowLabelStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  color: tokens[colorScheme].evidenceLabel,
  flexShrink: 0,
  m: 0,
  pt: '2px',
  textStyle: 'small-label',
  textTransform: 'uppercase',
  w: '152px',
})

export const metadataRowValueStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  color: tokens[colorScheme].evidenceValue,
  m: 0,
  textStyle: 'caption',
})
